import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import RoutineStyle from "../../../../components/Routine&Performance/RoutineUpload.module.css";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  styled,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import ProcessMapUploadForm from "../../Forms/ProcessMapUploadForm/ProcessMapUploadForm";
import { X } from "lucide-react";
import { apiService } from "../../../../api/apiService";
import useFunctionOptions from "../../utils/useFunctionOptions";
import SuccessPopup from "../../Popup/Success&ErrorPopup/SuccessPopup";
import ErrorPopup from "../../Popup/Success&ErrorPopup/ErrorPopup";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";

interface ProcessFile {
  id: string;
  name: string;
  date: string;
  functionL1: string;
  month: string;
  year: string;
  status: string;
  size: string;
}

interface CustomerCentricityPopups {
  openUpload: boolean;
  setOpenUpload: Dispatch<SetStateAction<boolean>>;
  management: string;
  verticalTabValue?: string;
  onSuccess?: () => void;
}

const StyledMenuItem = styled(MenuItem)({
  color: "black",
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
});

// Reusable Dropdown Component
interface CustomDropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (event: SelectChangeEvent<string>, child: React.ReactNode) => void;
  loading?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  loading,
}) => (
  <FormControl fullWidth sx={{ mb: 2 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      sx={{ borderRadius: "8px", color: "#000" }}
      value={value}
      label={label}
      onChange={onChange}
      disabled={loading}
    >
      <StyledMenuItem value="">Select {label}</StyledMenuItem>
      {options.map((option, index) => (
        <StyledMenuItem key={index} value={option}>
          {option}
        </StyledMenuItem>
      ))}
    </Select>
    {loading && (
      <CircularProgress
        size={20}
        sx={{
          position: "absolute",
          right: "30px",
          top: "30%",
          transform: "translateY(-50%)",
          zIndex: 1,
        }}
      />
    )}
  </FormControl>
);

// Reusable Text Input Component
interface CustomTextInputProps {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  InputProps?: any;
  InputLabelProps?: any;
}

const CustomTextInput: React.FC<CustomTextInputProps> = ({
  label,
  value,
  onChange,
  InputProps,
  InputLabelProps,
}) => (
  <TextField
    fullWidth
    sx={{ mb: 2 }}
    label={label}
    value={value}
    onChange={onChange}
    InputProps={InputProps}
    InputLabelProps={InputLabelProps}
  />
);

const CustomerCentricityPopup: React.FC<CustomerCentricityPopups> = ({
  openUpload,
  setOpenUpload,
  management,
  verticalTabValue,
  onSuccess,
}) => {
  const { instance } = useMsal(); // Get the MSAL instance for authentication
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [files, setFiles] = useState<ProcessFile[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [functionL1, setFunctionL1] = useState("");
  const [functionL2, setFunctionL2] = useState("");
  const [functionL3, setFunctionL3] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [uploading, setUploading] = useState<boolean>(false);

  // State for NPS Governance dropdown
  const [npsGovernanceOption, setNpsGovernanceOption] = useState("");

  // State for Stakeholder connects dropdown
  const [stakeholderConnectsOption, setStakeholderConnectsOption] =
    useState("");

  // State for Contact Model Platforms
  const [contactModelPlatformName, setContactModelPlatformName] = useState("");
  const [isCSATMeasured, setIsCSATMeasured] = useState("");

  // State for SLA Action Plans (Contact Model)
  const [hasSLAs, setHasSLAs] = useState("");

  // Use the custom hook to fetch options for each level
  const { options: l1Options, loading: l1Loading } = useFunctionOptions(
    "l1",
    ""
  );
  const { options: l2Options, loading: l2Loading } = useFunctionOptions(
    "l2",
    functionL1
  );
  const { options: l3Options, loading: l3Loading } = useFunctionOptions(
    "l3",
    functionL2
  );
  const { options: l4Options, loading: l4Loading } = useFunctionOptions(
    "l4",
    functionL3
  );

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from(new Array(10), (val, index) =>
    (currentYear - 1 + index).toString()
  );

  const handleSubmit = async () => {
    const formData = new FormData();

    let payload = {};
    if (verticalTabValue === "NPS Governance") {
      if (npsGovernanceOption === "NPS Governance for the GBS") {
        payload = {
          section_name: verticalTabValue,
          management: management,
          function_l1: functionL1,
          month: month,
          year: year,
          upload_type: npsGovernanceOption,
        };
      } else if (npsGovernanceOption === "Screenshot of the NPS tool") {
        payload = {
          section_name: verticalTabValue,
          management: management,
          upload_type: npsGovernanceOption,
        };
      }
    } else if (verticalTabValue === "Stakeholder connects") {
      payload = {
        section_name: verticalTabValue,
        management: management,
        function_l1: functionL1,
        month: month,
        year: year,
        upload_type: stakeholderConnectsOption,
      };
    } else if (verticalTabValue === "Contact Model Platform") {
      payload = {
        section_name: verticalTabValue,
        management: management,
        function_l1: functionL1,
        contact_model_platform_name: contactModelPlatformName,
        is_CSAT_measured: isCSATMeasured,
      };
    } else if (verticalTabValue === "SLA Action Plans (Contact Model)") {
      payload = {
        section_name: verticalTabValue,
        management: management,
        function_l1: functionL1,
        function_l2: functionL2,
        function_l3: functionL3,
        does_contact_model_platform_have_SLAs: hasSLAs,
      };
    } else if (
      verticalTabValue === "Process Identification - Service Request"
    ) {
      payload = {
        section_name: verticalTabValue,
        management: management,
      };
    } else {
      payload = {
        section_name: verticalTabValue,
        management: management,
        function_l1: functionL1,
      };
    }

    formData.append("cust_centricity", JSON.stringify(payload));
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    setUploading(true);

    try {
      const result = await apiService(
        "/management/customer_centricity",
        "POST",
        formData,
        undefined,
        instance as PublicClientApplication
      );
      console.log("Submission successful:", result);

      setFiles([
        {
          id: Math.random().toString(36).substr(2, 9),
          name: selectedFile ? selectedFile.name : "",
          date: new Date().toLocaleDateString(),
          functionL1,
          month,
          year,
          status: "Submitted",
          size: selectedFile
            ? `${Math.round(selectedFile.size / 1024)} KB`
            : "0 KB",
        },
        ...files,
      ]);

      setOpenUpload(false);
      resetUploadState();
      setShowSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error during submission:", error);
      setErrorMessage("Error during submission. Please try again.");
      setShowError(true);
    } finally {
      setUploading(false);
    }
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setFunctionL1("");
    setFunctionL2("");
    setFunctionL3("");
    setMonth("");
    setYear("");
    setNpsGovernanceOption("");
    setStakeholderConnectsOption("");
    setContactModelPlatformName("");
    setIsCSATMeasured("");
    setHasSLAs("");
  };

  const isFileFieldVisible = () => {
    if (verticalTabValue === "NPS Governance") {
      return npsGovernanceOption !== "";
    } else if (verticalTabValue === "Stakeholder connects") {
      return stakeholderConnectsOption !== "";
    } else if (verticalTabValue === "Contact Model Platform") {
      return functionL1 && contactModelPlatformName && isCSATMeasured;
    } else if (verticalTabValue === "SLA Action Plans (Contact Model)") {
      return functionL1 && functionL2 && functionL3 && hasSLAs === "Yes";
    } else if (
      verticalTabValue === "Process Identification - Service Request"
    ) {
      return true; // Always show file field for this tab
    } else {
      return functionL1;
    }
  };

  const getFileTitle = () => {
    switch (verticalTabValue) {
      case "NPS Governance":
        return "Provide documentation or governance frameworks that outline how NPS is measured and managed, including governance routines, leadership involvement, and reporting mechanisms. Evidence should include NPS survey reports and action plans.";

      case "Hotspots":
        return `Provide evidence of the "hotspot" concept being implemented, such as meeting notes, documentation, or dashboard reports showing areas of focus for improvement. Evidence should show follow-up actions based on identified hotspots.`;

      case "Contact Model Platforms":
        return "Provide evidence that the Contact Model Platform is in place and being used according to the standard, such as platform screenshots, usage statistics, or documentation showing alignment with the Contact Model standard. The GBS is using the global landscape for the contact model. (Has the global service catalog (L1-L4) been integrated into SNOW).";
      case "SLA Action Plans (Contact Model)":
        return "GBS must have a Contact Model Platform for handling requests according to the Global Service Catalogue standard, including SLAs, CSAT, and service quality indicators (CSAT, reopening, overdue/expired).";
      case "Process Identification - Service Request":
        return "GBS must have a Contact Model Platform for handling requests according to the Global Service Catalogue standard, including SLAs, CSAT, and service quality indicators (CSAT, reopening, overdue/expired).";
      default:
        return undefined;
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showError) {
      timer = setTimeout(() => {
        setShowError(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showError]);

  return (
    <>
      <Dialog
        open={openUpload}
        onClose={() => {
          setOpenUpload(false);
          resetUploadState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#000",
          }}
        >
          {verticalTabValue}
          <IconButton onClick={() => setOpenUpload(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Conditional Rendering for NPS Governance */}
            {verticalTabValue === "NPS Governance" && (
              <CustomDropdown
                label="NPS Governance Option *"
                value={npsGovernanceOption}
                options={[
                  "NPS Governance for the GBS",
                  "Screenshot of the NPS tool",
                ]}
                onChange={(e) => setNpsGovernanceOption(e.target.value)}
              />
            )}

            {/* Conditional Rendering for Stakeholder connects */}
            {verticalTabValue === "Stakeholder connects" && (
              <CustomDropdown
                label="Stakeholder Connects Option *"
                value={stakeholderConnectsOption}
                options={[
                  "Calendar invites with the stakeholder",
                  "The action plan for the detractors & Passives",
                ]}
                onChange={(e) => setStakeholderConnectsOption(e.target.value)}
              />
            )}

            {/* Conditional Rendering for Contact Model Platforms */}
            {verticalTabValue === "Contact Model Platform" && (
              <>
                <CustomDropdown
                  label="Function L1 *"
                  value={functionL1}
                  options={l1Options}
                  onChange={(e) => setFunctionL1(e.target.value)}
                />

                <CustomTextInput
                  label="Name the contact model platform to handle the request *"
                  value={contactModelPlatformName}
                  onChange={(e) => setContactModelPlatformName(e.target.value)}
                  InputProps={{
                    style: { color: "#000" },
                  }}
                  InputLabelProps={{
                    style: { color: "#000" },
                  }}
                />

                <CustomDropdown
                  label="Is CSAT Measured? *"
                  value={isCSATMeasured}
                  options={["Yes", "No"]}
                  onChange={(e) => setIsCSATMeasured(e.target.value)}
                />
              </>
            )}

            {/* Conditional Rendering for SLA Action Plans (Contact Model) */}
            {verticalTabValue === "SLA Action Plans (Contact Model)" && (
              <>
                <CustomDropdown
                  label="Function L1 *"
                  value={functionL1}
                  options={l1Options}
                  onChange={(e) => setFunctionL1(e.target.value)}
                  loading={l1Loading}
                />

                <CustomDropdown
                  label="Function L2 *"
                  value={functionL2}
                  options={l2Options}
                  onChange={(e) => setFunctionL2(e.target.value)}
                  loading={l2Loading}
                />

                <CustomDropdown
                  label="Function L3 *"
                  value={functionL3}
                  options={l3Options}
                  onChange={(e) => setFunctionL3(e.target.value)}
                  loading={l3Loading}
                />

                <CustomDropdown
                  label="Does the contact model platform have SLAs? *"
                  value={hasSLAs}
                  options={["Yes", "No"]}
                  onChange={(e) => {
                      const newValue = e.target.value;
                      setHasSLAs(newValue);
                      if (newValue === "No") {
                        setSelectedFile(null);
                        setUploadProgress(0);
                      }
                    }}
                  loading={l4Loading}
                />
              </>
            )}

            {/* Render Function L1, Month, Year fields for NPS Governance or Stakeholder connects */}
            {(verticalTabValue === "NPS Governance" &&
              npsGovernanceOption === "NPS Governance for the GBS") ||
            (verticalTabValue === "Stakeholder connects" &&
              stakeholderConnectsOption !== "") ? (
              <>
                <CustomDropdown
                  label="Function L1 *"
                  value={functionL1}
                  options={l1Options}
                  onChange={(e) => setFunctionL1(e.target.value)}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48% 48%",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomDropdown
                    label="Month *"
                    value={month}
                    options={[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ]}
                    onChange={(e) => setMonth(e.target.value)}
                  />

                  <CustomDropdown
                    label="Year *"
                    value={year}
                    options={years}
                    onChange={(e) => setYear(e.target.value)}
                  />
                </Box>
              </>
            ) : null}

            {/* Render Function L1 for Action Plans and Hotspots */}
            {(verticalTabValue === "Action Plans" ||
              verticalTabValue === "Hotspots") && (
              <CustomDropdown
                label="Function L1 *"
                value={functionL1}
                options={l1Options}
                onChange={(e) => setFunctionL1(e.target.value)}
              />
            )}

            {/* File Upload Section (Conditional Rendering) */}
            {isFileFieldVisible() && (
              <ProcessMapUploadForm
                file={selectedFile}
                onFileSelect={handleFileSelect}
                fileTitle={getFileTitle()}
                // fileTitle={
                //   verticalTabValue === 'Process Identification - Service Request'
                //     ? 'Please upload the current processes that have service request, current platforms used to handle tickets & service quality measurement'
                //     : undefined
                // }
                onFileRemove={() => {
                  setSelectedFile(null);
                  setUploadProgress(0);
                }}
                uploadProgress={uploadProgress}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenUpload(false)}
            sx={{ color: "#000", borderColor: "#000" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              (verticalTabValue === "SLA Action Plans (Contact Model)" &&
              hasSLAs === "No"
                ? false
                : !selectedFile) || uploading
            }
            sx={{
              color: "white",
              bgcolor: "black",
              "&:hover": { bgcolor: "black" },
              "&:disabled": { bgcolor: "grey.300" },
            }}
          >
            {uploading ? "Uploading..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Popup */}
      <SuccessPopup
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={`${verticalTabValue} uploaded successfully`}
      />

      {/* Error Popup */}
      <ErrorPopup
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </>
  );
};

export default CustomerCentricityPopup;
